import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Divider,
  Icon,
  Text,
  Progress as ChakraProgress,
  Image,
} from "@chakra-ui/react";
import AudioRecorder from "./chatAudioRecorder";
import gpuConstants from "./gpuConstants";
import Progress from "./progress";
import { BiMicrophone, BiSolidMicrophoneOff } from "react-icons/bi";
import { BiMicrophoneOff } from "react-icons/bi";

export const AudioSource = {
  URL: "URL",
  FILE: "FILE",
  RECORDING: "RECORDING",
};

export function GPUChatAudioManagerSpeechMode(props) {
  const { 
    mode, 
    transcriber, 
    onRecordingStateChange, 
    onRecordingComplete,
    onTranscript, // Add new prop for transcript callback
    setPlaceholder,
    setTyping,
    customStyle,
    customIcon,
    showLoading
  } = props;
  
  const [isRecording, setIsRecording] = useState(false);
  const [audioData, setAudioData] = useState(undefined);
  const [progress, setProgress] = useState(undefined);

  const isAudioLoading = progress !== undefined;

  // Use a ref to track the latest callback to avoid re-triggering useEffect when the callback identity changes
  const onRecordingStateChangeRef = useRef(onRecordingStateChange);
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => {
    onRecordingStateChangeRef.current = onRecordingStateChange;
    onTranscriptRef.current = onTranscript;
  }, [onRecordingStateChange, onTranscript]);

  // Notify parent when recording state changes
  useEffect(() => {
    if (onRecordingStateChangeRef.current) {
      onRecordingStateChangeRef.current(isRecording);
    }
  }, [isRecording]);

  const resetAudio = () => setAudioData(undefined);

  const setAudioFromRecording = async (data) => {
    // Only used in GPU mode
    resetAudio();
    setProgress(0);

    const blobUrl = URL.createObjectURL(data);
    const fileReader = new FileReader();
    fileReader.onprogress = (event) => {
      setProgress(event.loaded / event.total || 0);
    };
    fileReader.onloadend = async () => {
      const audioCTX = new AudioContext({ sampleRate: gpuConstants.SAMPLING_RATE });
      const arrayBuffer = fileReader.result;
      const decoded = await audioCTX.decodeAudioData(arrayBuffer);

      setProgress(undefined);
      setAudioData({
        buffer: decoded,
        url: blobUrl,
        source: AudioSource.RECORDING,
        mimeType: data.type,
      });

      // Notify parent (if provided) that a recording blob is available
      if (onRecordingComplete) {
        try { onRecordingComplete(data); } catch (e) { console.warn('onRecordingComplete callback failed', e); }
      }

      if (mode === "gpu") {
        // Kick off GPU transcription with decoded buffer
        transcriber.start(decoded);
      }
    };
    fileReader.readAsArrayBuffer(data);
  };

  const handleMicClick = () => {
    if (!isRecording) {
      // START
      setIsRecording(true);
      if (setPlaceholder) setPlaceholder("Listening ...");
      if (setTyping) setTyping(true);

      if (mode === "speech") {
        transcriber.start(); // begins SpeechRecognition
      }
      // GPU mode: AudioRecorder starts due to isRecording=true
    } else {
      // STOP
      setIsRecording(false);
      if (setPlaceholder) setPlaceholder("Processing ...");

      if (mode === "speech") {
        transcriber.stop(); // triggers onend -> emit final buffered text
      }
      // GPU mode: stopping will cause AudioRecorder to fire onRecordingComplete
    }
  };

  // Bind GPU results back to UI
  useEffect(() => {
    if (mode === "gpu" && transcriber.output && !transcriber.isBusy) {
      const t = transcriber.output.text || "";
      if (t) {
        if (setPlaceholder) setPlaceholder(t);
        if (setTyping) setTyping(false);
        
        // Call onTranscript callback with GPU transcript
        if (onTranscriptRef.current) {
          onTranscriptRef.current(t);
        }
      } else {
        if (setPlaceholder) setPlaceholder("");
        if (setTyping) setTyping(false);
      }
    }
  }, [mode, transcriber.output, transcriber.isBusy, setPlaceholder, setTyping]);

  // Listen for speech API output
  useEffect(() => {
    if (mode === "speech" && transcriber.output && !transcriber.isBusy) {
      const t = transcriber.output.text || "";
      if (t) {
        if (setPlaceholder) setPlaceholder(t);
        if (setTyping) setTyping(false);
        
        // Call onTranscript callback with speech API transcript
        if (onTranscriptRef.current) {
          onTranscriptRef.current(t);
        }
      }
    }
  }, [mode, transcriber.output, transcriber.isBusy, setPlaceholder, setTyping]);

  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      justifyContent="center" 
      alignItems="center"
      {...customStyle}
    >
      <HStack w="full">
        {showLoading ? (
          // Show loading indicator if provided
          <Box boxSize="30px" display="flex" alignItems="center" justifyContent="center">
            <Box 
              w="20px" 
              h="20px" 
              border="2px solid #E6E7E9"
              borderTop="2px solid #3182CE"
              borderRadius="full"
              animation="spin 1s linear infinite"
            />
          </Box>
        ) : customIcon ? (
          // Use custom icon if provided
          <Box onClick={handleMicClick} cursor="pointer">
            {customIcon}
          </Box>
        ) : (
          // Default icon
          <Image
            onClick={handleMicClick}
            boxSize="30px"
            opacity="0.5"
            cursor="pointer"
            src={isRecording ? "/assets/svgs/un-mute.svg" : "/assets/svgs/mute.svg"}
          />
        )}
      </HStack>

      {audioData && transcriber.progressItems.length > 0 && (
        <Box position="relative" zIndex={10} p={4} w="full">
          <Text mb={2}>Loading AIRA...</Text>
          <VStack spacing={2}>
            {transcriber.progressItems.map((data) => (
              <Box key={data.file} w="full">
                <Progress text={data.file} percentage={data.progress} />
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {isAudioLoading && <AudioDataBar progress={progress} />}

      {/* GPU mode recording pipe */}
      <AudioRecorder
        recording={mode === "gpu" ? isRecording : false} // only capture mic in GPU mode
        onRecordingComplete={setAudioFromRecording}
      />
    </Box>
  );
}

function VerticalBar() {
  return <Divider orientation="vertical" bg="gray.200" height="24px" />;
}

function AudioDataBar(props) {
  return <ProgressBar progress={`${Math.round(props.progress * 100)}%`} />;
}

function ProgressBar(props) {
  return (
    <ChakraProgress
      value={props.progress}
      size="xs"
      colorScheme="blue"
      borderRadius="full"
      w="full"
      transition="all 0.1s ease"
    />
  );
}

// Add CSS for spinner animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);